/**
 * useShellPerWeapon - PER-WEAPON attack state (v8.223.0).
 *
 * Custom mods, overrides and the last fire mode are REMEMBERED per armed
 * weapon instead of one global set that silently followed you from bolter to
 * chainsword. Keyed by weaponId (the macro precedent) for real combatants,
 * by index for the scratch rack. Local-only, never synced - see multiplayer.md.
 *
 * Returns the record and its setter (the fire-mode memory writes it), the
 * armed weapon's key, and its custom mods and overrides with their setters,
 * which do nothing while no weapon is armed. Extracted from DeathWatchSimulator.
 */
import { useState, useMemo, useCallback } from 'react';
import { EMPTY_WEAPON_OVERRIDES } from '../engine/weaponOverrides';
import { projectLoadout } from '../engine/roster/projection';

/** Stable identity: a new [] each render would defeat every memo downstream. */
const EMPTY_CUSTOM_MODS = [];

/** The per-weapon memory's key for whatever is armed right now, or null. */
export function armedKeyOf(actor, selectedSlot) {
  if (selectedSlot === null) return null;
  if (actor) return projectLoadout(actor).weaponIds[selectedSlot] ?? `idx-${selectedSlot}`;
  return `scratch-${selectedSlot}`;
}

/** One field of the armed weapon's record, or its empty value. */
function fieldOf(state, key, field, empty) {
  return key ? (state[key]?.[field] ?? empty) : empty;
}

/** A setter for one field of the armed weapon's record, accepting both updater forms. */
function useFieldSetter(setState, key, field, empty) {
  return useCallback(
    (next) => {
      if (!key) return;
      setState((p) => {
        const cur = p[key]?.[field] ?? empty;
        return { ...p, [key]: { ...p[key], [field]: typeof next === 'function' ? next(cur) : next } };
      });
    },
    [setState, key, field, empty],
  );
}

/** @param {object|null} actor @param {number|null} selectedSlot */
export default function useShellPerWeapon(actor, selectedSlot) {
  const [perWeaponAttackState, setPerWeaponAttackState] = useState({});
  const armedWeaponKey = useMemo(() => armedKeyOf(actor, selectedSlot), [actor, selectedSlot]);
  const attackCustomMods = useMemo(
    () => fieldOf(perWeaponAttackState, armedWeaponKey, 'customMods', EMPTY_CUSTOM_MODS),
    [perWeaponAttackState, armedWeaponKey],
  );
  const weaponOverrides = useMemo(
    () => fieldOf(perWeaponAttackState, armedWeaponKey, 'overrides', EMPTY_WEAPON_OVERRIDES),
    [perWeaponAttackState, armedWeaponKey],
  );
  const setAttackCustomMods = useFieldSetter(setPerWeaponAttackState, armedWeaponKey, 'customMods', EMPTY_CUSTOM_MODS);
  const setWeaponOverrides = useFieldSetter(
    setPerWeaponAttackState,
    armedWeaponKey,
    'overrides',
    EMPTY_WEAPON_OVERRIDES,
  );
  return {
    perWeaponAttackState,
    setPerWeaponAttackState,
    armedWeaponKey,
    attackCustomMods,
    weaponOverrides,
    setAttackCustomMods,
    setWeaponOverrides,
  };
}
